import { useSchoolClasses } from '@/composables/useSchoolClasses';
import { usePermissions } from '@/composables/usePermissions';
import { useRouteContext } from '@/composables/useRouteContext';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useLandingPreferenceStore } from '@/stores/useLandingPreferenceStore';
import { resolveTenantAdminHomeTarget } from '@/utils/routerHome.utils';
import { watch } from 'vue';
import { RouteLocationRaw, useRouter } from 'vue-router';
import { useCurrentTenant } from './schoolAdmin/useCurrentTenant';

export const useRouterHelper = () => {
  const { currentClass, syncCurrentClass } = useCurrentClass();
  const { areClassesOrGroupsPending } = useSchoolClasses();
  const { push } = useRouter();
  const { currentTenant } = useCurrentTenant();
  const { whoAmI } = useWhoAmI();
  const { hasProviderAccess, hasTenantAdminAccess } = usePermissions();
  const { isClassContext } = useRouteContext();
  const landingPreferenceStore = useLandingPreferenceStore();

  const waitForSchoolClasses = async (): Promise<void> => {
    if (areClassesOrGroupsPending.value) {
      await new Promise<void>((resolve) => {
        const stopWatch = watch(areClassesOrGroupsPending, (isPending) => {
          if (isPending) return;
          stopWatch();
          resolve();
        });
      });
    }

    if (!currentClass.value) {
      syncCurrentClass();
    }
  };

  /**
   * Navigates to the observation timeline of the current class.
   */
  const navigateToAllStudents = async (): Promise<void> => {
    await waitForSchoolClasses();

    if (currentClass.value) {
      await pushThroughError({
        name: '//class/[classId]/student/observation-timeline',
        params: { classId: currentClass.value.id },
      });
    } else {
      await pushThroughError({ name: '//waiting-room' });
    }
  };

  /**
   * Navigates to the default page based on the user's role
   * - Provider: User Overview
   * - Tenant Admin: User Overview or class, depending on the stored landing preference
   * - User: Class Setup or Students
   */
  const navigateToHome = async (): Promise<void> => {
    if (hasProviderAccess.value) {
      await pushThroughError({ name: '//provider/home/user' });
      return;
    }

    if (hasTenantAdminAccess.value && currentTenant.value) {
      await waitForSchoolClasses();

      const preferredLandingContext =
        landingPreferenceStore.getPreference(whoAmI.value?.id, currentTenant.value.id)?.context ?? null;
      const homeTarget = resolveTenantAdminHomeTarget({
        hasCurrentClass: !!currentClass.value,
        isClassContext: isClassContext.value,
        preferredLandingContext,
      });

      if (homeTarget === 'class') {
        await navigateToAllStudents();
        return;
      }

      await pushThroughError({
        name: '//school/[schoolId]/settings/user/',
        params: { schoolId: currentTenant.value.id },
      });
    } else {
      await navigateToAllStudents();
    }
  };

  /**
   * Navigates to the school onboarding, where users without a current school join or create one.
   * Use `replace` when leaving a school that no longer exists, so going back does not return to it.
   */
  const navigateToOnboarding = async (options?: { replace?: boolean }): Promise<void> => {
    await pushThroughError({ name: '/onboarding/school', replace: options?.replace });
  };

  /**
   * re-routing fails silently if a routing has taken place shortly after the page has loaded
   * this is a workaround to catch the error and push the route again
   */
  const pushThroughError = async (route: RouteLocationRaw): Promise<void> => {
    const err = await push(route);
    if (err) void push(route);
  };

  return {
    navigateToAllStudents,
    navigateToHome,
    navigateToOnboarding,
    pushThroughError,
  };
};
